import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ApiButton,
  Button,
  ContentTemplate,
  Grid,
  Icon,
  KeyValue,
  Pill,
  Progress,
  SectionHeader,
  TitleHeader,
} from 'web-library';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import NavigationButtons from '../../../components/NavigationButtons';
import RouteKey from '../../../routes/key';
import { ApplicationStatus } from '../../../api/candidate/exam/model';
import ReviewWithdrawalDetails from '../../../modules/withdrawal/ReviewWithdrawalDetails';
import ReviewWithdrawalCandidateParticulars from '../../../modules/withdrawal/ReviewWithdrawalCandidateParticulars';
import ReviewWithdrawalExamDetails from './ReviewWithdrawal/ReviewWithdrawalExamDetails';
import { useGetNonNationalExamWithdrawalSummary } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamWithdrawalSummary';
import {
  DownloadSummaryResponse,
  useGetNonNationalDownloadSummary,
} from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamWithdrawalDownloadSummary';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
({
  subheader: {
    marginBottom: theme.spacing(4),
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  personalDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  requestTable: {
    marginBottom: theme.spacing(8),
  },
  buttonsContainer: {
    marginTop: theme.spacing(-3),
    marginBottom: theme.spacing(7),
  },
  confirmButtonsContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  downloadPdf: {
    display: 'flex',
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
}));

const { exam, withdrawal } = RouteKey.child;
const { examRegistrations, nonNationalAdditionalDocuments } = RouteKey.withdrawal.child;

interface WithdrawalSummaryProps {
  isTransactionHistory?: boolean;
}

const WithdrawalSummary = ({ isTransactionHistory = false }: WithdrawalSummaryProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const { uuid } = useParams<{ uuid: string }>();
  const [shouldOpenPdf, setShouldOpenPdf] = useState<boolean>(false);

  const { data: withdrawalDetails } = useGetNonNationalExamWithdrawalSummary({ uuid });

  const {
    data: downloadSummaryPdfData,
    refetch: fetchSummaryPdf,
    isFetching: isDownloadingSummaryPdf,
    status: GetWithdrawalDownloadPDFStatus,
  } = useGetNonNationalDownloadSummary({
    uuid,
    onSuccess: (data: DownloadSummaryResponse) => {
      const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
  });

  const handleBack = () => {
    history.push(`/${exam}/${examRegistrations.route}`);
  };

  const handleAddDocument = () => {
    history.push(
      `/${withdrawal}/${examRegistrations.route}/${uuid}/${nonNationalAdditionalDocuments.route}`
    );
  };

  if (downloadSummaryPdfData && downloadSummaryPdfData.url && shouldOpenPdf) {
    window.open(downloadSummaryPdfData?.url, '_blank');
    setShouldOpenPdf(false);
  }

  const additionalComponentRenderer = () => {
    if (isDownloadingSummaryPdf) {
      return (
        <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />
      );
    }
    return (
      <ApiButton
        // className={classes.actionButton}
        variant="text"
        color="primary"
        status={GetWithdrawalDownloadPDFStatus}
        onClick={() => fetchSummaryPdf()}
        size="small"
        id="link-download"
      >
        <IconWithLabel
          iconName={IconName.download}
          iconSize="M"
          label={t('withdrawal->>ViewApplication.DownloadSummary')}
          typographyProps={{
            variant: 'h5',
          }}
        />
      </ApiButton>
    );
  };

  if (!withdrawalDetails) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader title={t('withdrawal->>ViewApplication.TitleHeader')} />
      <ContentTemplate isDividerBottom type="large" isContinuable>
        <Grid className={classes.personalDetailsContainer}>
          <Grid container direction="row" alignItems="flex-start">
            <SectionHeader
              title={t('withdrawal->>ViewApplication.ApplicationDetails')}
            >
              {additionalComponentRenderer()}
            </SectionHeader>
          </Grid>
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="applicationId"
                keyText={t('withdrawal->>ViewApplication.ApplicationId')}
                valueText={withdrawalDetails.applicationId}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="status"
                keyText={t('withdrawal->>ViewApplication.Status')}
                valueText={
                  <Pill
                    variant={
                      withdrawalDetails.status === ApplicationStatus.WITHDRAWN ? 'default' : 'info'
                    }
                    label={withdrawalDetails.status}
                  />
                }
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="applicationDateTime"
                keyText={t('withdrawal->>ViewApplication.ApplicationDateTime')}
                valueText={stringToLongDateAndShortTime(withdrawalDetails.applicationDateTime)}
              />
            </Grid>

            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="lastUpdatedDateTime"
                keyText={t('withdrawal->>ViewApplication.LastUpdatedDateTime')}
                valueText={stringToLongDateAndShortTime(withdrawalDetails.lastUpdatedDateTime)}
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentTemplate>
      <ReviewWithdrawalDetails withdrawalDetails={withdrawalDetails} isSectionHeader={false} />
      <ReviewWithdrawalCandidateParticulars withdrawalDetails={withdrawalDetails} />
      <ReviewWithdrawalExamDetails withdrawalDetails={withdrawalDetails} isDividerBottom={false} />
      <ContentTemplate continuous type="full">
        <Grid className={classes.buttonsContainer}>
          {isTransactionHistory ? (
            <Button
              id="button-selectSubject-back"
              variant="outlined"
              onClick={() =>
                history.push(`/${exam}/${examRegistrations.route}`, {
                  from: 'TransactionHistoryPages',
                })
              }
            >
              {t('exam->>RegistrationSummary.BackToTransactionHistory')}
            </Button>
          ) : (
            <NavigationButtons
              backLabel={t('withdrawal->>ViewApplication.Back')}
              procceedLabel={t('withdrawal->>ViewApplication.AddDocument')}
              onBackClick={handleBack}
              onProceedClick={handleAddDocument}
              showProceedButton={
                withdrawalDetails.status !== 'Withdrawn' && withdrawalDetails.allowedAddDocuments
              }
            />
          )}
        </Grid>
      </ContentTemplate>
    </>
  );
};

export default WithdrawalSummary;
